<template>
  <div class="sun-arc" :style="arcStyle">
    <svg
      ref="svgRef"
      :viewBox="`0 0 ${WIDTH} ${HEIGHT}`"
      class="sun-arc__svg"
      @pointermove="onMove"
      @pointerup="endDrag"
      @pointerleave="endDrag">
      <!-- Sky + ground -->
      <rect :x="0" :y="0" :width="WIDTH" :height="HORIZON_Y" class="sun-arc__sky" />
      <rect :x="0" :y="HORIZON_Y" :width="WIDTH" :height="HEIGHT - HORIZON_Y" class="sun-arc__ground" />
      <line :x1="MARGIN" :y1="HORIZON_Y" :x2="WIDTH - MARGIN" :y2="HORIZON_Y" class="sun-arc__horizon" />

      <!-- Sky dome + altitude grid -->
      <path :d="domePath" class="sun-arc__dome" />
      <g v-for="alt in [30, 60]" :key="alt">
        <line class="sun-arc__grid" :x1="MARGIN" :y1="altitudeToY(alt)" :x2="WIDTH - MARGIN" :y2="altitudeToY(alt)" />
        <text class="sun-arc__grid-label" :x="MARGIN + 4" :y="altitudeToY(alt) - 3">{{ alt }}°</text>
      </g>

      <!-- Decorative sun path (sunrise → solar noon → sunset) -->
      <path :d="sunPath" class="sun-arc__sun-path" />

      <!-- Min altitude window line (draggable) -->
      <line
        class="sun-arc__window-line"
        :x1="MARGIN"
        :y1="altitudeToY(min)"
        :x2="WIDTH - MARGIN"
        :y2="altitudeToY(min)" />
      <circle
        class="sun-arc__handle"
        :cx="WIDTH - MARGIN"
        :cy="altitudeToY(min)"
        :r="8"
        @pointerdown="startDrag($event)" />

      <!-- Live sun dot -->
      <template v-if="sunAltitude != null">
        <circle class="sun-arc__sun-halo" :cx="sunDot.x" :cy="sunDot.y" :r="11" />
        <circle
          class="sun-arc__sun"
          :cx="sunDot.x"
          :cy="sunDot.y"
          :r="6"
          :class="{ 'sun-arc__sun--down': sunAltitude < 0 }" />
      </template>
    </svg>

    <div class="sun-arc__readout">
      <span class="sun-arc__value">{{ tt('Min altitude', 'Najnižja višina') }} {{ min }}°</span>
      <span v-if="sunAltitude != null" class="sun-arc__sun-now"> ☀ {{ Math.round(sunAltitude) }}° </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { tt } from '../../../utils/consoleText';

const props = withDefaults(
  defineProps<{
    /** Minimum sun altitude for activation (0-90) */
    min: number;
    /** Live sun altitude (negative = below horizon) */
    sunAltitude?: number | null;
    /** Progress across the day 0 (sunrise) → 1 (sunset) for dot placement */
    sunProgress?: number;
  }>(),
  {
    sunAltitude: null,
    sunProgress: 0,
  },
);

const emit = defineEmits<{
  (e: 'update:min', value: number): void;
}>();

const WIDTH = 260;
const HEIGHT = 170;
const MARGIN = 22;
const HORIZON_Y = 142;
const DOME_R = 112;
const ARC_MIN_X = 20;
const ARC_MAX_X = 240;

const svgRef = ref<SVGSVGElement | null>(null);
const dragging = ref(false);

const arcStyle = {
  '--sun-arc-window': 'rgb(245, 158, 11)',
  '--sun-arc-sun-path': 'rgba(250, 204, 21, 0.5)',
};

function altitudeToY(altitude: number): number {
  return HORIZON_Y - (clamp(altitude, 0, 90) / 90) * DOME_R;
}

function eventToAltitude(event: PointerEvent): number {
  const rect = svgRef.value?.getBoundingClientRect();
  if (!rect || rect.height === 0) return 0;
  const svgY = ((event.clientY - rect.top) / rect.height) * HEIGHT;
  return clamp(((HORIZON_Y - svgY) / DOME_R) * 90, 0, 90);
}

function startDrag(event: PointerEvent) {
  dragging.value = true;
  (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
}

function onMove(event: PointerEvent) {
  if (!dragging.value) return;
  emit('update:min', Math.round(eventToAltitude(event)));
}

function endDrag() {
  dragging.value = false;
}

const domePath = computed(() => `M ${ARC_MIN_X} ${HORIZON_Y} A ${DOME_R} ${DOME_R} 0 0 1 ${ARC_MAX_X} ${HORIZON_Y}`);

/** Decorative sun arc — rises on the left, peaks at noon, sets on the right. */
const sunPath = computed(
  () => `M ${ARC_MIN_X + 8} ${HORIZON_Y} Q ${(ARC_MIN_X + ARC_MAX_X) / 2} 18 ${ARC_MAX_X - 8} ${HORIZON_Y}`,
);

const sunDot = computed(() => {
  const progress = clamp(props.sunProgress, 0, 1);
  const x = ARC_MIN_X + progress * (ARC_MAX_X - ARC_MIN_X);
  const y = props.sunAltitude != null ? altitudeToY(props.sunAltitude) : HORIZON_Y;
  return { x, y };
});

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
</script>

<style scoped>
.sun-arc {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.sun-arc__svg {
  width: 100%;
  max-width: 300px;
  touch-action: none;
  user-select: none;
  overflow: visible;
}

.sun-arc__sky {
  fill: rgba(56, 130, 246, 0.05);
}

.sun-arc__ground {
  fill: rgba(74, 222, 128, 0.05);
}

.sun-arc__horizon {
  stroke: rgba(148, 163, 184, 0.5);
  stroke-width: 1.5;
}

.sun-arc__dome {
  fill: none;
  stroke: rgba(148, 163, 184, 0.35);
  stroke-width: 1.5;
  stroke-dasharray: 4 4;
}

.sun-arc__grid {
  stroke: rgba(148, 163, 184, 0.2);
  stroke-width: 1;
  stroke-dasharray: 2 5;
}

.sun-arc__grid-label {
  fill: rgba(100, 116, 139, 0.75);
  font-size: 9px;
}

.sun-arc__sun-path {
  fill: none;
  stroke: var(--sun-arc-sun-path);
  stroke-width: 2;
}

.sun-arc__window-line {
  stroke: var(--sun-arc-window);
  stroke-width: 2;
  stroke-dasharray: 5 4;
}

.sun-arc__handle {
  fill: var(--sun-arc-window);
  stroke: #fff;
  stroke-width: 2;
  cursor: ns-resize;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
}

.sun-arc__sun-halo {
  fill: rgba(250, 204, 21, 0.28);
}

.sun-arc__sun {
  fill: #facc15;
  stroke: #b45309;
  stroke-width: 1;
}

.sun-arc__sun--down {
  opacity: 0.45;
}

.sun-arc__readout {
  display: flex;
  align-items: center;
  gap: 10px;
  font-variant-numeric: tabular-nums;
}

.sun-arc__value {
  font-size: 12px;
  font-weight: 700;
  color: rgb(245, 158, 11);
}

.sun-arc__sun-now {
  font-size: 11px;
  color: rgba(100, 116, 139, 0.9);
  background: rgba(250, 204, 21, 0.12);
  border-radius: 999px;
  padding: 2px 8px;
}
</style>
